import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Trans, useTranslation } from 'react-i18next';
import { FullScreenError as BaseFullScreenError } from '@asset-metrix/portal-ui';
import { useWindowEvent } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  HttpError,
  isMaintenanceModeEnabled,
  isNetworkError,
  isPortalAuthNetworkError,
  isPortalAuthServiceUnavailableError,
  isServiceUnavailableError,
} from '../../api/errors';
import { usePortalTheme } from '../../app/usePortalTheme';
import { useMaintenanceModeUnlock } from '../../hooks/useMaintenanceModeUnlock';
import { useStore } from '../../store';
import { HTTP_STATUS } from '../../utils/constants';
import { extractErrorMessage } from '../../utils/errorMessage';
import { getTenantNameBySubdomain } from '../../utils/getTenantNameBySubdomain';

import styles from './fullscreenerror.module.scss';

const MAINTENANCE_MODE_UNLOCK_PHRASE = 'letmein';

export function FullScreenError({ error }: { error: unknown }) {
  const { resetBoundary } = useErrorBoundary();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { tenantThemeLoaded } = usePortalTheme();

  const [, setMaintenanceModeUnlocked] = useMaintenanceModeUnlock();
  const [, setTypedKeys] = useState('');

  const [isSessionAuthenticated, logoutSession, tenantNameFromStore] = useStore(
    useShallow((state) => [
      state.isSessionAuthenticated(),
      state.logoutSession,
      state.tenantName,
    ])
  );

  let tenantNameForMessage: string | undefined =
    tenantNameFromStore ?? undefined;
  if (tenantNameForMessage == null) {
    try {
      tenantNameForMessage = getTenantNameBySubdomain();
    } catch {
      tenantNameForMessage = undefined;
    }
  }

  useWindowEvent('keydown', (evt) => {
    setTypedKeys((prev) => {
      const newKeys = (prev + evt.key).slice(
        -MAINTENANCE_MODE_UNLOCK_PHRASE.length
      );

      if (newKeys.toLowerCase() === MAINTENANCE_MODE_UNLOCK_PHRASE) {
        setMaintenanceModeUnlocked(true);
        globalThis.location.reload();
      }

      return newKeys;
    });
  });

  const logout = useCallback(async () => {
    resetBoundary();
    await logoutSession({ initiatedByUser: true });
  }, [logoutSession, resetBoundary]);

  const handleRetry = useCallback(() => {
    queryClient.removeQueries();
    resetBoundary();
  }, [resetBoundary, queryClient]);

  const isTenantNotFound =
    error instanceof HttpError && error.statusCode === HTTP_STATUS.NOT_FOUND;

  const isServiceUnavailable =
    isServiceUnavailableError(error) ||
    isPortalAuthServiceUnavailableError(error);

  const isNetworkBroken =
    isNetworkError(error) || isPortalAuthNetworkError(error);

  const inMaintenanceMode = isMaintenanceModeEnabled(error);
  let headline: string;
  let message: React.ReactNode;
  let imageClassName: string | undefined;

  if (isTenantNotFound) {
    headline = t('app.fullScreenError.tenantNotFound.headline');
    message = (
      <Trans
        i18nKey="app.fullScreenError.tenantNotFound.message"
        values={{ tenantName: tenantNameForMessage }}
      />
    );
  } else if (inMaintenanceMode) {
    imageClassName = 'maintenance-image';
    headline = t('app.fullScreenError.plannedMaintenance.headline');
    message = t('app.fullScreenError.plannedMaintenance.message');
  } else if (isServiceUnavailable) {
    imageClassName = 'maintenance-image';
    headline = t('app.fullScreenError.serviceUnavailable.headline');
    message = t('app.fullScreenError.serviceUnavailable.message');
  } else if (isNetworkBroken) {
    imageClassName = 'network-error-image';
    headline = t('app.fullScreenError.networkError.headline');
    message = t('app.fullScreenError.networkError.message');
  } else {
    headline = t('app.fullScreenError.loadingError');
    message = extractErrorMessage(error, t('app.fullScreenError.genericError'));
  }

  return (
    <BaseFullScreenError
      headline={headline}
      message={message}
      onRetry={handleRetry}
      retryLabel={t('app.fullScreenError.actions.retry')}
      onLogout={isSessionAuthenticated ? logout : undefined}
      logoutLabel={t('app.fullScreenError.actions.logout')}
      showLogo={tenantThemeLoaded}
    >
      {imageClassName && <div className={styles[imageClassName]} />}
    </BaseFullScreenError>
  );
}
